import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e", goldL:"#d4a853",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  teal:"#0d9488",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export function ClientAnalytics() {
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("top");
  const [topLimit, setTopLimit] = useState(25);
  
  useEffect(() => {
    const load = async () => {
      // Fetch ALL clients in batches (Supabase default limit is 1000)
      const fetchAll = async (table) => {
        const all = [];
        let from = 0;
        const PAGE = 1000;
        while (true) {
          const { data, error } = await supabase.from(table).select("*").range(from, from + PAGE - 1);
          if (error || !data || data.length === 0) break;
          all.push(...data);
          if (data.length < PAGE) break;
          from += PAGE;
        }
        return all;
      };
      const [c, p, a] = await Promise.all([
        fetchAll("clients"),
        fetchAll("payments"),
        fetchAll("appointments"),
      ]);
      setClients(c || []);
      setPayments(p || []);
      setAppointments(a || []);
      setLoading(false);
    };
    load();
  }, []);
  
  // Calculate real spending from payments
  const enrichedClients = clients.map(c => {
    const clientPayments = payments.filter(p => p.client_id === c.id);
    const realSpent = clientPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const totalVisits = appointments.filter(a => a.cid === c.id && a.status === "completed").length;
    const totalAppts = appointments.filter(a => a.cid === c.id).length;
    const noShows = appointments.filter(a => a.cid === c.id && a.status === "no-show").length;
    const cancelled = appointments.filter(a => a.cid === c.id && a.status === "cancelled").length;
    const lastApptDate = appointments.filter(a => a.cid === c.id).sort((x, y) => (y.date || "").localeCompare(x.date || ""))[0]?.date;
    
    // Days since last visit
    let daysSince = null;
    if (lastApptDate) {
      daysSince = Math.floor((new Date() - new Date(lastApptDate)) / (1000 * 60 * 60 * 24));
    }
    
    // Combined spent (real + imported)
    const combinedSpent = Math.max(realSpent, parseFloat(c.spent) || 0);
    
    // Auto-categorize
    let segment = "new";
    if (combinedSpent >= 5000) segment = "vip";
    else if (combinedSpent >= 1500) segment = "regular";
    else if (totalVisits === 0 && combinedSpent === 0) segment = "lead";
    else if (daysSince && daysSince > 180) segment = "atrisk";
    else if (totalVisits > 0) segment = "active";
    
    return {
      ...c,
      realSpent,
      combinedSpent,
      totalVisits,
      totalAppts,
      noShows,
      cancelled,
      noShowRate: totalAppts > 0 ? Math.round((noShows / totalAppts) * 100) : 0,
      lastApptDate,
      daysSince,
      segment,
    };
  });
  
  // Stats
  const totalRevenue = enrichedClients.reduce((s, c) => s + c.combinedSpent, 0);
  const avgLTV = enrichedClients.length > 0 ? totalRevenue / enrichedClients.length : 0;
  const segments = {
    vip: enrichedClients.filter(c => c.segment === "vip"),
    regular: enrichedClients.filter(c => c.segment === "regular"),
    active: enrichedClients.filter(c => c.segment === "active"),
    new: enrichedClients.filter(c => c.segment === "new"),
    lead: enrichedClients.filter(c => c.segment === "lead"),
    atrisk: enrichedClients.filter(c => c.segment === "atrisk"),
  };
  
  // Top spenders
  const topSpenders = [...enrichedClients].sort((a, b) => b.combinedSpent - a.combinedSpent).slice(0, topLimit);
  
  // At-risk clients (haven't visited in 180+ days, but spent over $500)
  const atRisk = enrichedClients
    .filter(c => c.daysSince && c.daysSince > 180 && c.combinedSpent > 500)
    .sort((a, b) => b.combinedSpent - a.combinedSpent);
  
  // VIPs (top 10% spenders)
  const vips = enrichedClients.filter(c => c.segment === "vip").sort((a, b) => b.combinedSpent - a.combinedSpent);
  
  // No-show offenders
  const noShowOffenders = enrichedClients
    .filter(c => c.noShows >= 2)
    .sort((a, b) => b.noShows - a.noShows);
  
  if (loading) return <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading client analytics...</div>;
  
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div>
        <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>📊 Client Analytics</div>
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>Insights and reports across all {enrichedClients.length.toLocaleString()} clients</div>
      </div>
      
      {/* KPI Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <KPI label="Total Revenue" value={"$"+Math.round(totalRevenue).toLocaleString()} color={C.gold} icon="💰"/>
        <KPI label="Avg LTV" value={"$"+Math.round(avgLTV).toLocaleString()} color={C.green} icon="📈"/>
        <KPI label="Total Clients" value={enrichedClients.length.toLocaleString()} color={C.blue} icon="👥"/>
        <KPI label="VIPs" value={vips.length} color={C.purple} icon="⭐" sub={vips.length>0?"$5K+ spent":""}/>
        <KPI label="At-Risk" value={atRisk.length} color={C.red} icon="⚠️" sub="180+ days inactive"/>
      </div>
      
      {/* Segments */}
      <div style={{padding:18,background:C.card,border:"1px solid "+C.border,borderRadius:12}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>Client Segmentation</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:8}}>
          {[
            ["VIP","vip",C.purple,"$5K+",segments.vip.length],
            ["Regular","regular",C.gold,"$1.5K-$5K",segments.regular.length],
            ["Active","active",C.green,"Recent visit",segments.active.length],
            ["New","new",C.blue,"<$1.5K spent",segments.new.length],
            ["Lead","lead",C.muted,"Never booked",segments.lead.length],
            ["At-Risk","atrisk",C.red,"180+ days",segments.atrisk.length],
          ].map(([label,key,color,desc,count])=>(
            <div key={key} onClick={()=>setTab(key)} style={{padding:"12px 14px",borderRadius:9,border:"1.5px solid "+(tab===key?color:C.border),background:tab===key?color+"15":"transparent",cursor:"pointer",transition:"all 0.15s"}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:4}}>{label}</div>
              <div style={{fontSize:22,fontWeight:800,color:color,fontFamily:S}}>{count}</div>
              <div style={{fontSize:10,color:C.dim,marginTop:2}}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Tabs */}
      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:"1px solid "+C.border,flexWrap:"wrap"}}>
        {[
          ["top","🏆 Top Spenders"],
          ["vip","⭐ VIPs"],
          ["atrisk","⚠️ At-Risk"],
          ["noshow","🚫 No-Show Offenders"],
          ["all","📋 All Clients"],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:"7px 14px",fontSize:12,fontWeight:tab===id?700:500,color:tab===id?"#fff":C.muted,background:tab===id?C.gold:"transparent",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F}}>{label}</button>
        ))}
      </div>
      
      {/* Tab Content */}
      {tab === "top" && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,display:"flex",justifyContent:"space-between",alignItems:"center",background:C.goldBg,flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>🏆 Top {topLimit} Highest Spenders</div>
              <div style={{fontSize:11,color:C.muted}}>Clients ranked by lifetime value</div>
            </div>
            <div style={{display:"flex",gap:5}}>
              {[10,25,50,100,500].map(n=>(
                <button key={n} onClick={()=>setTopLimit(n)} style={{padding:"5px 12px",fontSize:11,fontWeight:topLimit===n?700:500,color:topLimit===n?"#fff":C.muted,background:topLimit===n?C.gold:C.surface,border:"1px solid "+(topLimit===n?C.gold:C.border),borderRadius:7,cursor:"pointer",fontFamily:F}}>Top {n}</button>
              ))}
            </div>
          </div>
          <ClientList clients={topSpenders} showRank={true}/>
        </div>
      )}
      
      {tab === "vip" && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,background:C.purpleL}}>
            <div style={{fontSize:14,fontWeight:700,color:C.purple,fontFamily:S}}>⭐ VIP Clients ({vips.length})</div>
            <div style={{fontSize:11,color:C.muted}}>Lifetime spend $5,000+</div>
          </div>
          <ClientList clients={vips} showRank={true}/>
        </div>
      )}
      
      {tab === "atrisk" && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,background:C.redL}}>
            <div style={{fontSize:14,fontWeight:700,color:C.red,fontFamily:S}}>⚠️ At-Risk Clients ({atRisk.length})</div>
            <div style={{fontSize:11,color:C.muted}}>Spent $500+ but inactive for 180+ days. Win-back opportunities!</div>
          </div>
          <ClientList clients={atRisk} showDaysSince={true}/>
        </div>
      )}
      
      {tab === "noshow" && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,background:C.orangeL}}>
            <div style={{fontSize:14,fontWeight:700,color:C.orange,fontFamily:S}}>🚫 No-Show Offenders ({noShowOffenders.length})</div>
            <div style={{fontSize:11,color:C.muted}}>Clients with 2+ no-shows. Consider requiring deposits.</div>
          </div>
          <ClientList clients={noShowOffenders} showNoShows={true}/>
        </div>
      )}
      
      {tab === "all" && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>📋 All Clients ({enrichedClients.length})</div>
            <div style={{fontSize:11,color:C.muted}}>Sorted by lifetime value</div>
          </div>
          <ClientList clients={[...enrichedClients].sort((a,b)=>b.combinedSpent-a.combinedSpent).slice(0,500)} showRank={true}/>
          {enrichedClients.length>500&&<div style={{padding:"12px 18px",textAlign:"center",fontSize:11,color:C.muted,background:C.light}}>Showing top 500 - use Clients page for full list</div>}
        </div>
      )}
    </div>
  );
}

function KPI({label,value,color,icon,sub}) {
  return (
    <div style={{padding:"16px 18px",background:"#fff",border:"1px solid "+C.border,borderRadius:11,boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
        <span style={{fontSize:18}}>{icon}</span>
        <span style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</span>
      </div>
      <div style={{fontSize:24,fontWeight:800,color:color,fontFamily:S}}>{value}</div>
      {sub&&<div style={{fontSize:10,color:C.dim,marginTop:2}}>{sub}</div>}
    </div>
  );
}

function ClientList({clients,showRank,showDaysSince,showNoShows}) {
  if (clients.length === 0) return <div style={{padding:40,textAlign:"center",color:C.dim}}>No clients in this group</div>;
  
  return (
    <div style={{maxHeight:600,overflowY:"auto"}}>
      {clients.map((c,idx)=>(
        <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 18px",borderBottom:"1px solid "+C.border,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=C.light} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          {showRank && (
            <div style={{width:30,textAlign:"center",fontSize:14,fontWeight:800,color:idx===0?C.gold:idx<3?C.goldD:C.muted,fontFamily:S,flexShrink:0}}>
              {idx===0?"🥇":idx===1?"🥈":idx===2?"🥉":"#"+(idx+1)}
            </div>
          )}
          <div style={{width:36,height:36,borderRadius:9,background:C.goldBg,border:"1px solid "+C.borderGold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:C.gold,flexShrink:0}}>{c.name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
            <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {c.email||c.phone||"No contact"}
              {showDaysSince&&c.daysSince!==null&&<span style={{color:C.red,fontWeight:600}}> · {c.daysSince} days inactive</span>}
              {showNoShows&&<span style={{color:C.orange,fontWeight:600}}> · {c.noShows} no-shows</span>}
            </div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:15,fontWeight:800,color:C.gold,fontFamily:S}}>${Math.round(c.combinedSpent).toLocaleString()}</div>
            <div style={{fontSize:10,color:C.muted}}>{c.totalVisits} visits</div>
          </div>
        </div>
      ))}
    </div>
  );
}
